import { RotateCw, RotateCcw, Trash2, X } from "lucide-react";

/**
 * Floating size + rotation control for the currently-selected map asset.
 * Anchored to the asset's screen position via fixed `left/top` props.
 *
 * D&D 5e creature/object size categories (1 square = 5ft = grid_size px):
 *   Tiny=½sq, Small=1sq, Medium=1sq, Large=2sq, Huge=3sq, Gargantuan=4sq.
 */
const SIZE_PRESETS = [
  { key: "tiny",       label: "Tiny",       squares: 0.5, hint: "2½ ft" },
  { key: "small",      label: "Small",      squares: 1,   hint: "5 ft" },
  { key: "medium",     label: "Medium",     squares: 1,   hint: "5 ft" },
  { key: "large",      label: "Large",      squares: 2,   hint: "10 ft" },
  { key: "huge",       label: "Huge",       squares: 3,   hint: "15 ft" },
  { key: "gargantuan", label: "Gargantuan", squares: 4,   hint: "20 ft" },
];

export const VTTAssetSizePanel = ({
  asset,
  gridSize = 50,
  onChangeSize,
  onChangeRotation,
  onClose,
  onDelete,
  screen, // {x, y} in screen coords (top-right of asset)
}) => {
  if (!asset || !screen) return null;
  const currentSquares = Math.round((asset.size || gridSize) / gridSize * 2) / 2; // half-square steps

  const applyPreset = (squares) => onChangeSize?.(Math.round(squares * gridSize));

  return (
    <div
      className="fixed z-40 bg-[#0f0f12]/95 backdrop-blur-md border border-[#333] rounded-sm shadow-2xl p-2 flex flex-col gap-1.5"
      style={{ left: screen.x + 8, top: screen.y, minWidth: 200 }}
      onPointerDown={(e) => e.stopPropagation()}
      onWheel={(e) => e.stopPropagation()}
      data-vtt-sidebar="asset-size"
      data-testid="vtt-asset-size-panel"
    >
      <div className="flex items-center gap-2 px-1 pb-1 border-b border-[#222]">
        <span className="text-[10px] uppercase tracking-wider text-[#888] flex-1">Asset Size</span>
        <button onClick={onClose} className="text-[#666] hover:text-white" data-testid="vtt-asset-size-close"><X className="w-3 h-3" /></button>
      </div>

      <div className="grid grid-cols-3 gap-1">
        {SIZE_PRESETS.map(p => {
          const active = Math.abs(currentSquares - p.squares) < 0.25;
          return (
            <button
              key={p.key}
              onClick={() => applyPreset(p.squares)}
              className={`text-[10px] px-1.5 py-1 rounded-sm border ${active ? "bg-primary/20 border-primary text-primary" : "border-[#333] text-[#aaa] hover:border-primary/50 hover:text-white"}`}
              title={`${p.hint} · ${p.squares}sq`}
              data-testid={`vtt-asset-size-preset-${p.key}`}
            >
              {p.label}
              <span className="block text-[9px] text-[#666]">{p.squares}sq</span>
            </button>
          );
        })}
      </div>

      <div className="flex items-center gap-1 px-1 pt-1.5 border-t border-[#222]">
        <span className="text-[10px] text-[#888] flex-1">Squares</span>
        <input
          type="number"
          step="0.5"
          min="0.5"
          max="20"
          value={currentSquares}
          onChange={(e) => {
            const v = parseFloat(e.target.value);
            if (!isNaN(v) && v >= 0.5) onChangeSize?.(Math.round(v * gridSize));
          }}
          className="w-14 text-[11px] bg-[#1a1a1a] border border-[#333] rounded-sm px-1 py-0.5 text-white tabular-nums"
          data-testid="vtt-asset-size-squares-input"
        />
        <span className="text-[10px] text-[#666]">({Math.round(currentSquares * 5)} ft)</span>
      </div>

      <div className="flex items-center gap-1 px-1">
        <button onClick={() => onChangeRotation?.((asset.rotation || 0) - 15)} className="flex-1 text-[10px] flex items-center justify-center gap-1 py-1 rounded-sm border border-[#333] text-[#aaa] hover:border-primary/50 hover:text-white" data-testid="vtt-asset-rotate-ccw"><RotateCcw className="w-3 h-3" />-15°</button>
        <span className="text-[10px] text-[#888] tabular-nums w-10 text-center">{asset.rotation || 0}°</span>
        <button onClick={() => onChangeRotation?.((asset.rotation || 0) + 15)} className="flex-1 text-[10px] flex items-center justify-center gap-1 py-1 rounded-sm border border-[#333] text-[#aaa] hover:border-primary/50 hover:text-white" data-testid="vtt-asset-rotate-cw"><RotateCw className="w-3 h-3" />+15°</button>
      </div>

      {onDelete && (
        <button onClick={onDelete} className="text-[10px] flex items-center justify-center gap-1 py-1 rounded-sm text-red-400 hover:bg-red-500/10 mt-0.5" data-testid="vtt-asset-delete">
          <Trash2 className="w-3 h-3" /> Delete
        </button>
      )}
    </div>
  );
};

export default VTTAssetSizePanel;
